import {Component, Input, OnInit} from '@angular/core';
import {FacadeService} from '../../shared/service-facade/facade.service';
import {ReviewState} from "../../review/models/review-state";
import {ReviewService} from "../../review/services/review.service";
import {Promotion} from "../../shared/model/promotion";
import {PromotionVerificationRequest} from "falcon-api";
import {ApplicationLogDetailsRequest, ApplicationLogStepRequest, Log, Step} from "leia-api";
import {UtilitiesService} from "../../shared/services/utilities.service";
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import StateEnum = Step.StateEnum;
import NameEnum = Step.NameEnum;
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import { Configuration } from 'src/app/shared/configuration.enum';
import {FormBuilder, Validators} from "@angular/forms";
import {ComponentNameEnum} from "../../shared/model/component-name.enum";


@Component({
  selector: 'app-promo-code',
  templateUrl: './promo-code.component.html',
  styleUrls: ['./promo-code.component.scss']
})
export class PromoCodeComponent implements OnInit {
  @Input() reviewState: ReviewState = ReviewState.Initial;

  promoCodeErrorMessage = "";
  appliedPromocodes: string[] = [];
  appliedPromoCodeList: Array<Promotion> = [];
  reviewStates = ReviewState;
  showPromocodeSection: boolean = false;
  promoCodeForm = this.formBuilder.group({
    promoCode: ['', [Validators.required]]
  });

  constructor(
    private formBuilder: FormBuilder,
    private facadeService: FacadeService,
    private reviewService: ReviewService,
    private utils: UtilitiesService,
  ) {
  }

  ngOnInit(): void {
    this.appliedPromoCodeList = this.facadeService.getAppliedPromotions();
    this.appliedPromocodes.push(this.facadeService.getShoppingCartApplicationLogsRequest()?.promoCodes!);
  }

  /**
   * Add promo code to the application.
   * if promo code already exist, show error message.
   * @param value
   */
  onClickOfApply(value: any) {
    if (!this.promoCodeForm.valid) return;

    this.promoCodeErrorMessage = "";
    //To check if the promocode is already exist or not.If exist, show appropriate message.
    if (this.appliedPromoCodeList.findIndex(promotion => promotion.promoCode === value) > -1) {
      this.promoCodeErrorMessage = "Promo code already applied.";
      return;
    }
    //Service to verify the promocode is valid or not.
    this.facadeService.verifyPromocode(this.buildPromotionVerificationRequest(value)).subscribe(response => {
      if (!response) return;
      //If response valid, set to applicationLogRequest and update application log.
      if (response.valid!) {
        const promoCodeMessage = `Promotional code ${value} has been applied - ${response.message!}.`
        this.appliedPromocodes = value;
        let promotion = new Promotion(value, promoCodeMessage);
        this.appliedPromoCodeList = [];
        this.appliedPromoCodeList.push(promotion);

        // Check if promo code has an associated TISA URL, if so, set new value in configuration
        if (response.tisaUrl) {
          this.facadeService.addStorageItem(Configuration.TISA_URL, response.tisaUrl);
        }

        //Update applicationlogrequest object
        const applicationLogsRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
        applicationLogsRequest.promoCodes = this.appliedPromocodes.toString();
        this.facadeService.setShoppingCartApplicationLogsRequest(applicationLogsRequest);

        //To update applicationLog
        this.facadeService.deletePromoCode("").then(() => {
          this.buildApplicationLogRequests("Promo code Applied.");
        });
        this.facadeService.setAppliedPromotions(this.appliedPromoCodeList!);
      } else {
        this.promoCodeErrorMessage = "You have entered an invalid or expired promo code.";
      }
    });
  }

  /**
   * On cancel, remove the promocode from the application and also from session storage.
   * @param selectedPromocode
   */
  onClickOfCancel() {
    //to delete the promocode from the application_promotion table
    this.facadeService.deletePromoCode("").then(() => {
      //To update the shopping cart object with deleted item
      const applicationLogsRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
      this.appliedPromocodes = [];
      applicationLogsRequest.promoCodes = '';
      this.facadeService.setShoppingCartApplicationLogsRequest(applicationLogsRequest);

      this.facadeService.addStorageItem(
        Configuration.TISA_URL,
        this.facadeService.getConfigurationSetting(Configuration.ORIGINAL_TISA_URL)
      );

      this.buildApplicationLogRequests("Promo code removed.");
    });

    //Update the application promotion list
    this.appliedPromoCodeList = [];
    this.facadeService.setAppliedPromotions(this.appliedPromoCodeList!);
    this.promoCodeErrorMessage = "";
    this.showPromocodeSection = false;
    if (this.reviewState === ReviewState.Edit) {
      this.reviewState = ReviewState.ReadOnly;

      this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.PromoCode.toString() && val[1] === 0),1)
      this.reviewService.editStateMapChanged.emit();
    }
  }

  /***
   * Put the component in edit mode - Review page
   */
  editDetails() {
    this.reviewState = ReviewState.Edit;

    this.reviewService.editStateArray.push([ComponentNameEnum.PromoCode.toString(), 0])
    this.reviewService.editStateMapChanged.emit();
    this.showPromocodeSection = true;
  }

  /**
   * Close the edit mode.
   */
  close() {
    this.promoCodeErrorMessage = "";
    this.reviewState = ReviewState.ReadOnly;

    this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.PromoCode.toString() && val[1] === 0),1)
    this.reviewService.editStateMapChanged.emit();
    this.showPromocodeSection = false;
  }

  onClickOfAdd() {
    this.showPromocodeSection = true;
  }

  private buildPromotionVerificationRequest(promocode: string): PromotionVerificationRequest {
    let promotionVerificationRequest = new PromotionVerificationRequest();
    promotionVerificationRequest.brandId = Number(this.facadeService.getStorageItem("brandId"));
    promotionVerificationRequest.productUID = this.facadeService.getStorageItem("productUid");
    promotionVerificationRequest.promotionCode = promocode;
    return promotionVerificationRequest;
  }

  async buildApplicationLogRequests(logMessage: string) {

    let logs: Log[] = [];
    let applicationLogDetailsRequests: ApplicationLogDetailsRequest[] = [];

    let log = new Log();
    log.source = "BB8";
    log.timestamp = new Date().toISOString();
    log.details = logMessage;

    logs.push(log);
    applicationLogDetailsRequests.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, logMessage));
    const applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();

    applicationRequest.step = this.utils.createStep(NameEnum.ApplyPromotionalCode, StateEnum.Success, logs);
    applicationRequest.applicationLogStepRequest = this.utils.createApplicationLogStepRequest(OnboardingStepEnum.ApplyPromotionalCode, OnboardingStateEnum.Success, OnboardingSourceEnum.Bb8, applicationLogDetailsRequests);
    this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
    await this.facadeService.updateApplication();
  }
}
